import { useState, useEffect } from 'react';
import { exemplosReframing, dicasReframing, perguntasPoderosas } from '../../../mocks/worksheet-ressignificacao';

interface Reframing {
  situacao: string;
  significadoAtual: string;
  intencaoPositiva: string;
  contexto: string;
  novoSignificado: string;
  perguntasSelecionadas: string[];
}

export default function PraticaReframing() {
  const [reframingAtual, setReframingAtual] = useState(0);
  const [reframings, setReframings] = useState<Reframing[]>([
    {
      situacao: '',
      significadoAtual: '',
      intencaoPositiva: '',
      contexto: '',
      novoSignificado: '',
      perguntasSelecionadas: []
    }
  ]);
  const [dicaAleatoria] = useState(() => dicasReframing[Math.floor(Math.random() * dicasReframing.length)]);

  useEffect(() => {
    const saved = localStorage.getItem('worksheet-reframing');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.reframings) setReframings(data.reframings);
        if (data.reframingAtual !== undefined) setReframingAtual(data.reframingAtual);
      } catch (e) {
        console.error('Erro ao carregar dados salvos');
      }
    }
  }, []);

  useEffect(() => {
    const autoSave = setInterval(() => {
      localStorage.setItem('worksheet-reframing', JSON.stringify({
        reframings,
        reframingAtual
      }));
    }, 30000);

    return () => clearInterval(autoSave);
  }, [reframings, reframingAtual]);

  const calcularProgresso = () => {
    const atual = reframings[reframingAtual];
    let progresso = 0;
    if (atual.situacao.length > 20) progresso += 15;
    if (atual.significadoAtual.length > 20) progresso += 15;
    if (atual.intencaoPositiva.length > 20) progresso += 20;
    if (atual.contexto.length > 20) progresso += 20;
    if (atual.novoSignificado.length > 20) progresso += 20;
    if (atual.perguntasSelecionadas.length >= 2) progresso += 10;
    return progresso;
  };

  const adicionarReframing = () => {
    if (reframings.length < 5) {
      setReframings([...reframings, {
        situacao: '',
        significadoAtual: '',
        intencaoPositiva: '',
        contexto: '',
        novoSignificado: '',
        perguntasSelecionadas: []
      }]);
      setReframingAtual(reframings.length);
    }
  };

  const carregarExemplo = () => {
    const exemplo = exemplosReframing[Math.floor(Math.random() * exemplosReframing.length)];
    const novosReframings = [...reframings];
    novosReframings[reframingAtual] = {
      situacao: exemplo.situacao,
      significadoAtual: exemplo.significadoAtual,
      intencaoPositiva: exemplo.intencaoPositiva,
      contexto: exemplo.contexto,
      novoSignificado: exemplo.novoSignificado,
      perguntasSelecionadas: exemplo.perguntasPoderosas.slice(0, 2)
    };
    setReframings(novosReframings);
  };

  const atualizarCampo = (campo: keyof Reframing, valor: string | string[]) => {
    const novosReframings = [...reframings];
    novosReframings[reframingAtual] = {
      ...novosReframings[reframingAtual],
      [campo]: valor
    };
    setReframings(novosReframings);
  };

  const togglePergunta = (pergunta: string) => {
    const atual = reframings[reframingAtual];
    const perguntas = atual.perguntasSelecionadas.includes(pergunta)
      ? atual.perguntasSelecionadas.filter(p => p !== pergunta)
      : [...atual.perguntasSelecionadas, pergunta];
    atualizarCampo('perguntasSelecionadas', perguntas);
  };

  const progresso = calcularProgresso();

  return (
    <section id="pratica-reframing" className="py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Prática Interativa
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transforme até 5 situações limitantes em perspectivas empoderadoras
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-2">
              {reframings.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setReframingAtual(index)}
                  className={`w-10 h-10 rounded-full font-semibold transition-all duration-300 flex items-center justify-center ${
                    reframingAtual === index
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-110'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              {reframings.length < 5 && (
                <button
                  onClick={adicionarReframing}
                  className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-600 transition-all duration-300 flex items-center justify-center"
                >
                  <i className="ri-add-line text-xl"></i>
                </button>
              )}
            </div>

            <button
              onClick={carregarExemplo}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap flex items-center gap-2 text-sm"
            >
              <i className="ri-lightbulb-line"></i>
              Ver Exemplo
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progresso do Reframing {reframingAtual + 1}</span>
              <span className="text-sm font-bold text-purple-600">{progresso}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 transition-all duration-500 rounded-full"
                style={{ width: `${progresso}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-focus-3-line text-rose-600"></i>
                Passo 1: Identifique a Situação
              </label>
              <textarea
                value={reframings[reframingAtual].situacao}
                onChange={(e) => atualizarCampo('situacao', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Descreva uma situação específica que gera emoção negativa ou limitação..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{reframings[reframingAtual].situacao.length}/500 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-emotion-unhappy-line text-purple-600"></i>
                Passo 2: Significado Atual (Limitante)
              </label>
              <textarea
                value={reframings[reframingAtual].significadoAtual}
                onChange={(e) => atualizarCampo('significadoAtual', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="O que essa situação significa para você agora? Como você a interpreta?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{reframings[reframingAtual].significadoAtual.length}/500 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-heart-3-line text-pink-600"></i>
                Passo 3: Intenção Positiva
              </label>
              <p className="text-sm text-gray-600 mb-3 bg-pink-50 p-3 rounded-lg">
                Todo comportamento tem uma intenção positiva. Pergunte-se: "O que essa reação está tentando proteger ou conquistar para mim?"
              </p>
              <textarea
                value={reframings[reframingAtual].intencaoPositiva}
                onChange={(e) => atualizarCampo('intencaoPositiva', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Qual é a intenção positiva por trás dessa interpretação? O que ela está tentando proteger?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{reframings[reframingAtual].intencaoPositiva.length}/500 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-compass-3-line text-blue-600"></i>
                Passo 4: Reframing de Contexto
              </label>
              <p className="text-sm text-gray-600 mb-3 bg-blue-50 p-3 rounded-lg">
                Em que contexto ou situação esse comportamento seria útil, apropriado ou até uma vantagem?
              </p>
              <textarea
                value={reframings[reframingAtual].contexto}
                onChange={(e) => atualizarCampo('contexto', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Em que contexto isso seria considerado positivo ou útil?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{reframings[reframingAtual].contexto.length}/500 caracteres</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-question-line text-emerald-600"></i>
                Passo 5: Perguntas Poderosas "E se..."
              </label>
              <p className="text-sm text-gray-600 mb-3 bg-emerald-50 p-3 rounded-lg">
                Selecione 2-3 perguntas que expandem suas possibilidades de interpretação:
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {perguntasPoderosas.map((pergunta, index) => (
                  <label
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      reframings[reframingAtual].perguntasSelecionadas.includes(pergunta)
                        ? 'bg-emerald-100 border-2 border-emerald-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={reframings[reframingAtual].perguntasSelecionadas.includes(pergunta)}
                      onChange={() => togglePergunta(pergunta)}
                      className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 flex-1">{pergunta}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-amber-600"></i>
                Passo 6: Novo Significado Empoderador
              </label>
              <p className="text-sm text-gray-600 mb-3 bg-amber-50 p-3 rounded-lg">
                Agora escreva o novo significado que você escolhe dar a essa situação. Faça com que seja empoderador e libertador.
              </p>
              <textarea
                value={reframings[reframingAtual].novoSignificado}
                onChange={(e) => atualizarCampo('novoSignificado', e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Qual é o novo significado empoderador que você escolhe dar a essa situação?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{reframings[reframingAtual].novoSignificado.length}/500 caracteres</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-2xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-2">💡 Dica do Dia</h4>
              <p className="text-white/90 leading-relaxed">{dicaAleatoria}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}